import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import TourButton from '../../TourButton/TourButton';
import { getCalendarEventModalSteps } from '../../../tours/calendarEventModal';
import { useAppContext } from '../../../context/AppContext';
import axios from 'axios';
import './CalendarEventModal.css';
import LoadingOverlay from '../../LoadingOverlay/LoadingOverlay';
import AddClientModal from '../AddClientModal/AddClientModal';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import MuiSelect from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import { useNotification } from '../../notifications/useNotification';
import { muiSelectSx, muiMenuItemSx, sanitizeUrl } from '../../../utils/functions';
import AuthenticatedImage from '../../AuthenticatedImage/AuthenticatedImage';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import 'dayjs/locale/en-gb';
import { capitalize } from '../../../util';

const EVENT_TYPES = [
    'INITIAL_CONSULTATION',
    'FOLLOW_UP_CONSULTATION',
    'NUTRITION_ASSESSMENT',
    'BODY_COMPOSITION_ANALYSIS',
    'SPORTS_NUTRITION_CONSULTATION',
    'LONG_TERM_PROGRESS_REVIEW',
    'OTHER',
];

const calendarTheme = createTheme({
    palette: {
        primary: {
            main:  '#fd4701',
            light: '#fc9f5b',
            dark:  '#830102',
        },
    },
});

const datePickerSx = {
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#fc9f5b',
    },
};

/**
 * CalendarEventModal — redesigned version.
 *
 * Props mirror the original CalendarEventModal:
 *   setShowModal — callback to close the modal
 *   date         — optional prefilled start date (JS Date / string)
 *   event        — optional existing event object (edit mode)
 *   onSubmit     — async callback called after a successful save/delete
 */
const CalendarEventModal = ({ setShowModal, date, event, onSubmit }) => {
    const { t } = useTranslation();
    const { dietist, currentLanguage } = useAppContext();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const { showSuccess, showError } = useNotification();
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // ── Client selector ────────────────────────────────────────────
    const [clients, setClients]               = useState([]);
    const [clientSearch, setClientSearch]     = useState('');
    const [clientFocused, setClientFocused]   = useState(false);
    const [selectedClient, setSelectedClient] = useState(event?.client ?? null);
    const [showAddClient, setShowAddClient]   = useState(false);

    useEffect(() => {
        const fetchClients = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/dietists/${dietist._id}/clients`,
                    { params: { limit: 20 }, withCredentials: true }
                );
                setClients(res.data.clients ?? []);
            } catch (err) {
                console.error('Failed to load clients', err);
            }
        };
        fetchClients();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const adapterLocale = currentLanguage === 'it' ? 'it' : 'en-gb';

    const [formData, setFormData] = useState({
        startDate: date
            ? dayjs(date)
            : event?.startDate ? dayjs(event.startDate) : null,
        endDate: date
            ? dayjs(date).add(30, 'minute')
            : event?.endDate ? dayjs(event.endDate) : null,
        description: event?.description ?? '',
        type:        event?.type        ?? '',
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const validateForm = () => {
        const newErrors = {};
        const today = dayjs().startOf('day');

        if (!formData.startDate)
            newErrors.startDate = t('errorMessages.dateRequired');
        else if (formData.startDate.startOf('day').isBefore(today))
            newErrors.startDate = t('errorMessages.dateCantBeLowerThanToday');
        else if (formData.endDate && formData.startDate.isAfter(formData.endDate))
            newErrors.startDate = t('errorMessages.startDateCantBeGreaterThanEndDate');

        if (!formData.endDate)
            newErrors.endDate = t('errorMessages.dateRequired');
        else if (formData.endDate.startOf('day').isBefore(today))
            newErrors.endDate = t('errorMessages.dateCantBeLowerThanToday');
        else if (formData.startDate && formData.endDate.isBefore(formData.startDate))
            newErrors.endDate = t('errorMessages.endDateCantBeLowerThanStartDate');

        if (formData.description.length > 500)
            newErrors.description = t('errorMessages.too-long');

        if (!formData.type)
            newErrors.type = t('errorMessages.event-type-required');

        if (!selectedClient)
            newErrors.client = t('errorMessages.client-required');

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        try {
            setLoading(true);
            const payload = {
                ...formData,
                startDate: formData.startDate?.toISOString(),
                endDate:   formData.endDate?.toISOString(),
                dietist:   dietist._id,
                client:    selectedClient?._id,
            };
            if (event) {
                await axios.put(
                    `${process.env.REACT_APP_BACKEND_URL}/calendarEvents/${event._id}`,
                    payload,
                    { withCredentials: true }
                );
                showSuccess(t('event-updated'));
            } else {
                await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/calendarEvents`,
                    payload,
                    { withCredentials: true }
                );
                showSuccess(t('event-created'));
            }
            await onSubmit();
            handleClose();
        } catch (error) {
            console.error('Calendar event modal error:', error);
            showError(t('event-not-saved'));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/calendarEvents/${event._id}`,
                { withCredentials: true }
            );
            showSuccess(t('event-deleted'));
            await onSubmit();
            handleClose();
        } catch (error) {
            console.error('Delete event error:', error);
            showError(t('event-not-deleted'));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({ startDate: null, endDate: null, description: '', type: '' });
        setSelectedClient(null);
        setClientSearch('');
        setShowModal(false);
    };

    return (
        <>
            <LoadingOverlay open={loading} />

            {/* ── Add Client sub-modal ── */}
            {showAddClient && (
                <AddClientModal
                    setShowModal={setShowAddClient}
                    onSave={async (newClient) => {
                        try {
                            const res = await axios.get(
                                `${process.env.REACT_APP_BACKEND_URL}/dietists/${dietist._id}/clients`,
                                { params: { limit: 20 }, withCredentials: true }
                            );
                            const refreshed = res.data.clients ?? [];
                            setClients(refreshed);
                            if (newClient?._id) {
                                const found = refreshed.find(c => c._id === newClient._id);
                                if (found) setSelectedClient(found);
                            }
                        } catch (err) {
                            console.error('Failed to refresh clients', err);
                        }
                        setShowAddClient(false);
                    }}
                />
            )}

            {/* ── Overlay ── */}
            <div className="cem-overlay" onClick={handleClose}>

                {/* ── Modal container ── */}
                <div className="cem-modal" onClick={e => e.stopPropagation()} data-tour="cem-modal">

                    {/* ── Header ── */}
                    <div className="cem-header">
                        <div className="cem-header-text">
                            <h2 className="cem-title">
                                {event ? t('edit-event') : t('add-event')}
                            </h2>
                            <p className="cem-subtitle">
                                {event ? t('edit-event-subtitle') : t('add-event-subtitle')}
                            </p>
                        </div>
                        <div className='cem-tutorial-btn'>
                            <TourButton steps={getCalendarEventModalSteps(t)} iconOnly={isMobile} />
                        </div>
                        <button className="cem-close-btn" onClick={handleClose} aria-label="Close">
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>

                    {/* ── Body (scrollable) ── */}
                    <div className="cem-body">
                        <ThemeProvider theme={calendarTheme}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>

                            {/* Start & End date */}
                            <div className="cem-row" data-tour="cem-dates">
                                <div className="cem-field">
                                    <label className="cem-label">{t('start-date')}</label>
                                    <DateTimePicker
                                        value={formData.startDate}
                                        onChange={(val) => handleDateChange('startDate', val)}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                error: !!errors.startDate,
                                                helperText: errors.startDate,
                                                sx: datePickerSx,
                                            },
                                        }}
                                    />
                                </div>
                                <div className="cem-field">
                                    <label className="cem-label">{t('end-date')}</label>
                                    <DateTimePicker
                                        value={formData.endDate}
                                        onChange={(val) => handleDateChange('endDate', val)}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                error: !!errors.endDate,
                                                helperText: errors.endDate,
                                                sx: datePickerSx,
                                            },
                                        }}
                                    />
                                </div>
                            </div>

                            {/* ── Client selector ── */}
                            <div className="cem-field" data-tour="cem-client">
                                <label className="cem-label">{capitalize(t('client'))}</label>
                                {selectedClient ? (
                                    <div className="cem-client-chip">
                                        <AuthenticatedImage
                                            className="cem-client-avatar"
                                            src={sanitizeUrl(selectedClient.profilePhotoUrl)}
                                            fallbackSrc='/images/anonymous_avatar_icon.jpg'
                                            alt={`${selectedClient.name} ${selectedClient.surname}`}
                                        />
                                        <span className="cem-client-name">
                                            {selectedClient.name} {selectedClient.surname}
                                        </span>
                                        <button
                                            className="cem-client-clear"
                                            onClick={() => setSelectedClient(null)}
                                            aria-label="Remove client"
                                        >
                                            <span className="material-symbols-outlined">close</span>
                                        </button>
                                    </div>
                                ) : (
                                    <div className="cem-client-search-wrap">
                                        <div className="cem-client-search-input-wrap">
                                            <span className="material-symbols-outlined cem-search-icon">search</span>
                                            <input
                                                className={`cem-client-search-input${errors.client ? ' cem-input-error' : ''}`}
                                                type="text"
                                                placeholder={t('search-client')}
                                                value={clientSearch}
                                                onChange={e => setClientSearch(e.target.value)}
                                                onFocus={() => setClientFocused(true)}
                                                onBlur={() => setTimeout(() => setClientFocused(false), 150)}
                                            />
                                        </div>
                                        {(clientFocused || clientSearch.trim()) && (
                                            <div className="cem-client-dropdown">
                                                <button
                                                    type="button"
                                                    className="cem-client-option cem-client-new"
                                                    onClick={() => {
                                                        setClientSearch('');
                                                        setShowAddClient(true);
                                                    }}
                                                >
                                                    <span className="material-symbols-outlined">person_add</span>
                                                    <span>{t('add-new-client')}</span>
                                                </button>
                                                {clients
                                                    .filter(c => {
                                                        const q = clientSearch.toLowerCase().trim();
                                                        if (!q) return true;
                                                        return (
                                                            c.name?.toLowerCase().includes(q) ||
                                                            c.surname?.toLowerCase().includes(q)
                                                        );
                                                    })
                                                    .map(c => (
                                                        <button
                                                            key={c._id}
                                                            type="button"
                                                            className="cem-client-option"
                                                            onClick={() => {
                                                                setSelectedClient(c);
                                                                setClientSearch('');
                                                            }}
                                                        >
                                                            <AuthenticatedImage
                                                                className="cem-client-avatar"
                                                                src={sanitizeUrl(c.profilePhotoUrl)}
                                                                fallbackSrc='/images/anonymous_avatar_icon.jpg'
                                                                alt={`${c.name} ${c.surname}`}
                                                            />
                                                            <span>{c.name} {c.surname}</span>
                                                        </button>
                                                    ))
                                                }
                                            </div>
                                        )}
                                        {errors.client && (
                                            <span className="cem-field-error">{errors.client}</span>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Event type */}
                            <div className="cem-field" data-tour="cem-type">
                                <label className="cem-label">{t('event-type')}</label>
                                <FormControl fullWidth size="small" error={!!errors.type}>
                                    <MuiSelect
                                        label={t('event-type')}
                                        name="type"
                                        value={formData.type}
                                        onChange={handleInputChange}
                                        sx={muiSelectSx}
                                    >
                                        {EVENT_TYPES.map(type => (
                                            <MenuItem key={type} value={type} sx={muiMenuItemSx}>
                                                {t(`eventTypes.${type}`)}
                                            </MenuItem>
                                        ))}
                                    </MuiSelect>
                                    {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
                                </FormControl>
                            </div>

                            {/* Description */}
                            <div className="cem-field">
                                <label className="cem-label">{`${capitalize(t('notes'))} (${capitalize(t('optional'))})`}</label>
                                <TextField
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    placeholder={t('enter-description')}
                                    size="small"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    error={!!errors.description}
                                    helperText={errors.description}
                                    sx={datePickerSx}
                                />
                            </div>

                        </LocalizationProvider>
                        </ThemeProvider>
                    </div>

                    {/* ── Footer ── */}
                    <div className="cem-footer" data-tour="cem-footer">
                        <button className="cem-btn cem-btn-cancel" onClick={handleClose}>
                            {t('Cancel')}
                        </button>
                        <div className="cem-footer-right">
                            {event && (
                                <button className="cem-btn cem-btn-delete" onClick={handleDelete}>
                                    {t('Delete')}
                                </button>
                            )}
                            <button className="cem-btn cem-btn-save" onClick={handleSubmit}>
                                {event ? t('Save') : t('add-event-confirm-btn')}
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};

export default CalendarEventModal;
